/**
 * Assessment wire ⇄ domain mappers.
 *
 * The Tax Assessment Service `TaxAssessmentResponse` is a flat snake_case
 * object. Most fields round-trip through the generic deep converter, but a few
 * backend names differ from the UI domain shape and are fixed up explicitly:
 *   - `tax_refund_amount`  → `refundAmount`
 *   - `tax_payable_amount` → `demandAmount`
 *   - `created_at`         → `createdDate`
 *   - `updated_at`         → `lastUpdated`
 *
 * Decimal fields arrive as strings or numbers depending on the JSON encoder;
 * numeric fields are coerced to `number` so the UI can format them.
 *
 * NOTE: confirm field names against the live tax-assessment-service contract
 * during real-mode verification and adjust the overrides here only.
 */

import { toCamelDeep, toSnakeDeep } from '@/services/http/caseMap'
import type {
  Assessment,
  CreateAssessmentInput,
  UpdateAssessmentInput,
  TaxCalculationInput,
  TaxCalculationResult,
  AssessmentDocument,
} from '@/types/assessment.types'

const NUMERIC_FIELDS: (keyof Assessment)[] = [
  'grossIncome',
  'grossTotalIncome',
  'totalDeductions',
  'taxableIncome',
  'taxOnIncome',
  'totalTaxLiability',
  'taxPaid',
  'refundAmount',
  'demandAmount',
]

function toNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isNaN(n) ? undefined : n
}

export function mapAssessmentFromApi(raw: Record<string, unknown>): Assessment {
  const camel = toCamelDeep<Record<string, unknown>>(raw)

  // Backend → domain field-name fixups.
  if ('taxRefundAmount' in camel && !('refundAmount' in camel)) {
    camel.refundAmount = camel.taxRefundAmount
  }
  if ('taxPayableAmount' in camel && !('demandAmount' in camel)) {
    camel.demandAmount = camel.taxPayableAmount
  }
  if ('createdAt' in camel && !('createdDate' in camel)) {
    camel.createdDate = camel.createdAt
  }
  if ('updatedAt' in camel && !('lastUpdated' in camel)) {
    camel.lastUpdated = camel.updatedAt
  }

  // Coerce numeric (Decimal) fields to numbers.
  for (const field of NUMERIC_FIELDS) {
    if (field in camel) {
      const n = toNumber(camel[field])
      if (n === undefined) delete camel[field]
      else camel[field] = n
    }
  }

  // Drop zero refund/demand so the UI only shows meaningful figures.
  if (camel.refundAmount === 0) delete camel.refundAmount
  if (camel.demandAmount === 0) delete camel.demandAmount

  return camel as unknown as Assessment
}

export function mapAssessmentListFromApi(raw: unknown): Assessment[] {
  const arr = Array.isArray(raw)
    ? raw
    : ((raw as { items?: unknown[] })?.items ?? [])
  return (arr as Record<string, unknown>[]).map(mapAssessmentFromApi)
}

export function mapCreateAssessmentToApi(
  input: CreateAssessmentInput
): Record<string, unknown> {
  const body = toSnakeDeep<Record<string, unknown>>(input)
  if (!body.due_date) {
    const assessmentYearStart = /^(20\d{2})-\d{2}$/.exec(input.assessmentYear)?.[1]
    if (assessmentYearStart) body.due_date = `${assessmentYearStart}-07-31`
  }
  return body
}

export function mapUpdateAssessmentToApi(
  input: UpdateAssessmentInput
): Record<string, unknown> {
  return toSnakeDeep(input)
}

export function mapTaxCalculationToApi(
  input: TaxCalculationInput
): Record<string, unknown> {
  return toSnakeDeep(input)
}

export function mapTaxCalculationFromApi(
  raw: Record<string, unknown>
): TaxCalculationResult {
  const camel = toCamelDeep<Record<string, unknown>>(raw)
  const numericKeys = [
    'grossIncome',
    'totalDeductions',
    'taxableIncome',
    'taxOnIncome',
    'surcharge',
    'educationCess',
    'totalTaxLiability',
  ]
  for (const key of numericKeys) {
    camel[key] = toNumber(camel[key]) ?? 0
  }
  return camel as unknown as TaxCalculationResult
}

export function mapAssessmentDocumentFromApi(raw: Record<string, unknown>): AssessmentDocument {
  return toCamelDeep<AssessmentDocument>(raw)
}

export function mapAssessmentDocumentListFromApi(raw: unknown): AssessmentDocument[] {
  const arr = Array.isArray(raw)
    ? raw
    : ((raw as { items?: unknown[] })?.items ?? [])
  return (arr as Record<string, unknown>[]).map(mapAssessmentDocumentFromApi)
}

